// @ts-nocheck
import axios from "axios";
import {
  ICreateTeamRequest,
  IInviteMemberRequest,
  ITeamMembers,
  ITeamNotificationUser,
  ITeamPerformanceOverview,
  IUpdateTeamRequest,
  TeamMembersRoles,
} from "@/integration/team/interfaces/team.interface";
import { createService } from "@/integration/factory/service-factory.ts";
import { BaseResponse } from "@/integration/application/interfaces/base.ts";
import { ISearchPagination } from "@/integration/application/interfaces/searchPagination.interface.ts";
import { IGameDTO } from "@/integration/game/interfaces/game.interface.ts";
import { ISendInviteUserToTeamRequest, ITeam } from "@integration/team/interfaces/ITeam.ts";

/**
 * Service for team-related API calls
 * Refactored to use the service-factory for better error handling and request cancellation
 */
export default function teamService() {
  // Create base services for teams endpoints
  const teamsService = createService("/teams");
  const publicTeamsService = createService("/public/teams");

  const postCreate = async (data: ICreateTeamRequest): Promise<ITeam> => {
    try {
      const response =  await teamsService.create(data);

      return response;
    } catch (error) {
      console.error("Error creating team:", error);
      // Return a default response instead of propagating the error
      throw new Error("Failed to create team. Please try again later.");
    }
  };

  const putUpdate = async (
    teamId: string,
    data: IUpdateTeamRequest
  ): Promise<ITeam> => {
    try {
      if (!teamId) {
        throw new Error("Team ID is required");
      }
      return await teamsService.update<ITeam>(teamId, {id: teamId, ...data});
    } catch (error) {
      console.error("Error updating team:", error);
      // Return a default response instead of propagating the error
      throw new Error("Failed to update team. Please try again later.");
    }
  };

  const getProfileTeam = async (
    teamId: string
  ): Promise<BaseResponse<ITeam>> => {
    try {
      if (!teamId) {
        return { data: null, message: "Team ID is required", status: "error" } as BaseResponse<ITeam>;
      }
      const response = await teamsService.getById<BaseResponse<ITeam>>(teamId);

      return response;
    } catch (error) {
      console.error("Error getting team profile:", error);
      // Return a default response instead of propagating the error
      return { data: null, message: "Error fetching team profile", status: "error" } as BaseResponse<ITeam>;
    }
  };

  const getProfileTeamTag = async (
    tag: string
  ): Promise<BaseResponse<ITeam>> => {
    try {
      if (!tag) {
        return { data: null, message: "Team tag is required", status: "error" } as BaseResponse<ITeam>;
      }
      const response = await publicTeamsService.customGet<BaseResponse<ITeam>>(`/pro/${tag}`);

      return response;
    } catch (error) {
      console.error("Error getting team profile:", error);
      // Return a default response instead of propagating the error
      return { data: null, message: "Error fetching team profile", status: "error" } as BaseResponse<ITeam>;
    }
  };

  const postSendInvite = async (teamId: string, data: IInviteMemberRequest) => {
    try {
      if (!teamId) {
        return { success: false, message: "Team ID is required" };
      }
      return await teamsService.customPost(`/${teamId}/invite`, data);
    } catch (error) {
      console.error("Error sending invitation:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error sending invitation" };
    }
  };

  const postSendInvites = async (teamId: string, data: IInviteMemberRequest[]) => {
    try {
      if (!teamId) {
        return { success: false, message: "Team ID is required" };
      }
      return await teamsService.customPost(`/${teamId}/invites`, data);
    } catch (error) {
      console.error("Error sending multiple invitations:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error sending invitations" };
    }
  };

  const deleteTeamMemberByUserId = async (id: string, teamMemberId: string) => {
    try {
      if (!id || !teamMemberId) {
        return { success: false, message: "Team ID and team member ID are required" };
      }
      return await teamsService.customDelete(`/${id}/team-member/${teamMemberId}`);
    } catch (error) {
      console.error("Error deleting team member:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error deleting team member" };
    }
  };

  const patchRoleTeamMember = async (
    teamId: string,
    teamMemberId: string,
    newRole: TeamMembersRoles
  ) => {
    try {
      if (!teamId || !teamMemberId) {
        return { success: false, message: "Team ID and team member ID are required" };
      }
      return await teamsService.customPatch(
        `/${teamId}/team-member/${teamMemberId}`, {newRole}
      );
    } catch (error) {
      console.error("Error updating team member role:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error updating team member role" };
    }
  };

  const patchPositionTeamMember = async (
    teamId: string,
    teamMemberId: string,
    rosterStatus: string
  ) => {
    try {
      if (!teamId || !teamMemberId) {
        return { success: false, message: "Team ID and team member ID are required" };
      }
      return await teamsService.customPatch(
        `/${teamId}/assign/${teamMemberId}/roster`, {rosterStatus}
      );
    } catch (error) { //roster
      console.error("Error updating team member position:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error updating team member position" };
    }
  };

  const getInviteNotificationsUser = async (): Promise<
    ITeamNotificationUser[]
  > => {
    try {
      return await teamsService.customGet<ITeamNotificationUser[]>("/invitations/user");
    } catch (error) {
      console.error("Error getting user invitations:", error);
      // Return an empty array instead of propagating the error
      return [];
    }
  };

  const patchAcceptionInvitation = async (
    teamId: string,
    invitationId: string
  ) => {
    try {
      if (!teamId || !invitationId) {
        return { success: false, message: "Team ID and invitation ID are required" };
      }
      return await teamsService.customPatch(
        `/${teamId}/invitations/${invitationId}/accept`
      );
    } catch (error) {
      console.error("Error accepting invitation:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error accepting invitation" };
    }
  };

  const patchDeclineInvitation = async (
    teamId: string,
    invitationId: string
  ) => {
    try {
      if (!teamId || !invitationId) {
        return { success: false, message: "Team ID and invitation ID are required" };
      }
      return await teamsService.customPatch(
        `/${teamId}/invitations/${invitationId}/decline`
      );
    } catch (error) {
      console.error("Error declining invitation:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error declining invitation" };
    }
  };

  const deleteInvitation = async (
    teamId: string,
    invitationId: string
  ) => {
    try {
      return await teamsService.customDelete(
        `/${teamId}/invitations/${invitationId}`
      );
    } catch (error) {
      console.error("Error declining invitation:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error declining invitation" };
    }
  };

  const getTeamMembers = async (
    teamId: string
  ): Promise<BaseResponse<ITeamMembers>[]> => {
    try {
      if (!teamId) {
        console.error("Team ID is required");
        return [];
      }
      return await teamsService.customGet<BaseResponse<ITeamMembers>[]>(`/${teamId}/members`);
    } catch (error) {
      console.error("Error getting team members:", error);
      // Return an empty array instead of propagating the error
      return [];
    }
  };

  const getGamesTeam = async (
    teamId: string
  ): Promise<IGameDTO[]> => {
    try {
      if (!teamId) {
        console.error("Team ID is required");
        return [];
      }
      return await teamsService.customGet<IGameDTO[]>(`/${teamId}/games`);
    } catch (error) {
      console.error("Error getting team games:", error);
      // Return an empty array instead of propagating the error
      return [];
    }
  };

  const searchTeamByNameAndTag = async (searchPagination: ISearchPagination
  ): Promise<BaseResponse<ITeam>[]> => {
    const params = {
      search: searchPagination.search,
      page: searchPagination.page,
      limit: searchPagination.limit,
      order: searchPagination.order,
      entityId: searchPagination.entityId
    };

    try {
      return await publicTeamsService.customGet<BaseResponse<ITeam>[]>("/options/search", { params });
    } catch (error) {
      console.error("Error searching teams:", error);
      // Return an empty array instead of propagating the error
      return [];
    }
  };

  const searchMyTeamsByNameAndTag = async (searchPagination: ISearchPagination
  ): Promise<BaseResponse<ITeam>[]> => {
    const params = {
      search: searchPagination.search,
      page: searchPagination.page,
      limit: searchPagination.limit,
      order: searchPagination.order,
      gameId: searchPagination.gameId,
      entityId: searchPagination.entityId
    };

    try {
      return await teamsService.customGet<BaseResponse<ITeam>[]>("/options/user/search", { params });
    } catch (error) {
      console.error("Error searching my teams:", error);
      // Return an empty array instead of propagating the error
      return [];
    }
  };

  const getTeamByIdPublic = async (
    teamId: string
  ): Promise<BaseResponse<ITeam>> => {
    try {
      if (!teamId) {
        return { data: null, message: "Team ID is required", status: "error" } as BaseResponse<ITeam>;
      }
      return await publicTeamsService.getById<BaseResponse<ITeam>>(teamId);
    } catch (error) {
      console.error("Error getting team by ID:", error);
      // Return a default response instead of propagating the error
      return { data: null, message: "Error fetching team", status: "error" } as BaseResponse<ITeam>;
    }
  };

  const getStatsTeamByIdPublic = async (
    teamId: string
  ): Promise<ITeamPerformanceOverview | null> => {
    try {
      if (!teamId) {
        console.error("Team ID is required");
        return null;
      }
      return await publicTeamsService.customGet<ITeamPerformanceOverview>(`/${teamId}/stats`, {
        params: { businessType: 'TEAM' }
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return null;
    }
  };

  const getTeamMembersByIdPublic = async (
    teamId: string
  ): Promise<BaseResponse<ITeamMembers>[]> => {
    try {
      if (!teamId) {
        console.error("Team ID is required");
        return [];
      }
      return await publicTeamsService.customGet<BaseResponse<ITeamMembers>[]>(`/${teamId}/members`);
    } catch (error) {
      console.error("Error getting team members by ID:", error);
      // Return an empty array instead of propagating the error
      return [];
    }
  };

  const postInviteUserToTeam = async (
    teamId: string,
    request: ISendInviteUserToTeamRequest
  ) => {
    try {
      if (!teamId) {
        return { success: false, message: "Team ID is required" };
      }
      return await teamsService.customPost(
        `/${teamId}/applyToTeam`, request
      );
    } catch (error) {
      console.error("Error inviting user to team:", error);
      return { success: false, message: "Error inviting user to team" };
    }
  };

  const patchChangeVisibility = async (
    teamId: string,
    statusVisibilityMembers: boolean
  ) => {
    try {
      if (!teamId) {
        return { success: false, message: "Team ID is required" };
      }
      return await teamsService.customPatch(
        `/${teamId}/members-visibily`, { statusVisibilityMembers }
      );
    } catch (error) {
      console.error("Error changing team visibility:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error changing team visibility" };
    }
  };

  const patchChangeRecruitmentStatus = async (
    teamId: string,
    isRecruiting: boolean
  ) => {
    try {
      if (!teamId) {
        return { success: false, message: "Team ID is required" };
      }
      return await teamsService.customPatch(
        `/${teamId}/recruitment-status`, { isRecruiting }
      );
    } catch (error) {
      console.error("Error changing recruitment status:", error);
      // Return a default response instead of propagating the error
      return { success: false, message: "Error changing recruitment status" };
    }
  };

  const getTeamChampionshipRegistrations = async (
    teamId: string,
    page: number = 1,
    limit: number = 10
  ) => {
    try {
      if (!teamId) {
        console.error("Team ID is required");
        return { data: [], page: 1, limit, total: 0, totalPage: 0 };
      }

      const params = {
        page,
        limit
      };

      // This endpoint is in a different service (championships), so we need to use axios directly
      const response = await axios.get(
        `/championships/registration/team/${teamId}/paginated`,
        { params }
      );

      return response.data;
    } catch (error) {
      console.error("Error getting team championship registrations:", error);
      // Return a default paginated response instead of propagating the error
      return { data: [], page, limit, total: 0, totalPage: 0 };
    }
  };

  return {
    postCreate,
    getProfileTeam,
    postSendInvite,
    postSendInvites,
    getInviteNotificationsUser,
    patchAcceptionInvitation,
    patchDeclineInvitation,
    getTeamMembers,
    getTeamByIdPublic,
    getTeamMembersByIdPublic,
    putUpdate,
    searchTeamByNameAndTag,
    deleteTeamMemberByUserId,
    patchRoleTeamMember,
    patchPositionTeamMember,
    getGamesTeam,
    getStatsTeamByIdPublic,
    searchMyTeamsByNameAndTag,
    getProfileTeamTag,
    postInviteUserToTeam,
    deleteInvitation,
    patchChangeVisibility,
    patchChangeRecruitmentStatus,
    getTeamChampionshipRegistrations
  };
}
